import { useMemo } from "react";
import type { Instruction } from "@/routes/simulator/types";
import type { SimulatorReleaseLite } from "@/routes/simulator/types";

type PolicyInput = string | Instruction[] | unknown;

function parsePolicy(policy: PolicyInput): Instruction[] {
  try {
    if (typeof policy === "string") {
      const parsed = JSON.parse(policy);
      return Array.isArray(parsed) ? (parsed as Instruction[]) : [];
    }
    return Array.isArray(policy) ? (policy as Instruction[]) : [];
  } catch {
    return [];
  }
}

function isEnabledTrue(val: unknown): boolean {
  return val === true || val === "true";
}

function getEnabledTopicsFromReleasePolicy(policy: PolicyInput): string[] {
  const instructions = parsePolicy(policy);

  const topics = instructions
    .filter(
      (i: Instruction) =>
        i?.type === "isEnabled" &&
        isEnabledTrue(i.content) &&
        typeof i.topic === "string" &&
        i.topic.trim() !== ""
    )
    .map((i) => i.topic!.trim());

  return Array.from(new Set(topics));
}

function getVersionNumber(
  releases: SimulatorReleaseLite[],
  target: SimulatorReleaseLite
): number {
  const sorted = [...releases].sort(
    (a, b) =>
      new Date(a.createdAt ?? 0).getTime() -
      new Date(b.createdAt ?? 0).getTime()
  );
  return sorted.findIndex((r) => r.id === target.id) + 1;
}

function getLiveRelease(
  releases?: SimulatorReleaseLite[] | null
): SimulatorReleaseLite | null {
  if (!releases) return null;
  return releases.find((r) => r.status === "published") ?? null;
}

export function useLiveEnabledTopics(
  releases?: SimulatorReleaseLite[] | null
): {
  topics: string[];
  count: number;
  version: number | null;
} {
  return useMemo(() => {
    const live = getLiveRelease(releases);
    if (!live) {
      return { topics: [], count: 0, version: null };
    }
    const topics = getEnabledTopicsFromReleasePolicy(live.releasePolicy);
    const version = getVersionNumber(releases || [], live);
    return { topics, count: topics.length, version };
  }, [releases]);
}
